import { useTranslation } from "react-i18next"

import { useMutation as useApolloMutation } from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import { MutationSchema } from "shared/api/types"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { CreateFollowUpConfigurationInput } from "shared/types/__generated__/graphql"
import {
  CreateFollowUpConfigurationInputSchema,
  FollowUpConfigurationSchema
} from "shared/types/__generated__/validation"

import z from "zod"

import {
  CreateFollowUpConfigurationDocument,
  CreateFollowUpConfigurationMutation,
  CreateFollowUpConfigurationMutationVariables
} from "../graphql/mutations.generated"
import { FollowUpConfigurationsDocument } from "../graphql/queries.generated"

const CreateFollowUpConfigurationVariablesSchema = z.object({
  input: CreateFollowUpConfigurationInputSchema()
})
const CreateFollowUpConfigurationResponseSchema = MutationSchema.extend({
  followUpConfiguration: FollowUpConfigurationSchema()
})

export function useCreateFollowUpConfiguration() {
  const { t } = useTranslation()
  const [apolloMutate, rest] = useApolloMutation<
    CreateFollowUpConfigurationMutation,
    CreateFollowUpConfigurationMutationVariables
  >(CreateFollowUpConfigurationDocument)

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t(
        "Request.FollowUpConfiguration.CREATE_FOLLOW_UP_CONFIGURATION.Start"
      ),
      success: t(
        "Request.FollowUpConfiguration.CREATE_FOLLOW_UP_CONFIGURATION.Success"
      ),
      failure: t(
        "Request.FollowUpConfiguration.CREATE_FOLLOW_UP_CONFIGURATION.Failure"
      )
    }
  })

  const createFollowUpConfiguration = useEvent(
    (input: CreateFollowUpConfigurationInput) => {
      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()

      return callMutation(apolloMutate, {
        variables: { input },
        transform: data => data.followUpConfiguration,
        schemas: {
          input: CreateFollowUpConfigurationVariablesSchema,
          response: CreateFollowUpConfigurationResponseSchema
        },
        apolloOptions: {
          refetchQueries: [{ query: FollowUpConfigurationsDocument }],
          awaitRefetchQueries: true,
          onCompleted: raiseSuccessToast,
          onError: raiseErrorToast
        }
      })
    }
  )

  return { createFollowUpConfiguration, ...rest }
}
